import React, { useEffect, useMemo, useState } from 'react';
import { BACKEND_URL } from '../config/canonical';
import {
  NxButton, NxPanel, NxChip, NxTickerTile, NxApplicantCard, NxTimeline, NxTimelineItem, NxAlertStrip,
} from '../components/nx';
import {
  Briefcase, ClipboardCheck, MessagesSquare, ShieldCheck, BadgeCheck,
  X, RefreshCw, Send, Calendar, FileSignature, KeyRound, AlertTriangle,
  ChevronRight, Search, Filter, Mail, Award, Plus, Siren, Archive, Users, Download,
  GraduationCap, ScrollText, CheckSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ApplicantDetailDrawer from './recruitment/ApplicantDetailDrawer';

const BUCKETS = [
  { id: 'ALL',             label: 'All Applicants',  stages: null /* matches every stage */, tone: 'cyan',  icon: <Users size={14} /> },
  { id: 'OPEN_PIPELINE',   label: 'Open Positions',  stages: ['APPLIED', 'UNDER_REVIEW'], tone: 'blue', icon: <Briefcase size={14} /> },
  { id: 'TESTING_QUEUE',   label: 'Testing Queue',   stages: ['APPROVED_FOR_TESTING', 'TEST_IN_PROGRESS', 'AWAITING_TEST_REVIEW'], tone: 'amber', icon: <ClipboardCheck size={14} /> },
  { id: 'INTERVIEW_QUEUE', label: 'Interview Queue', stages: ['INTERVIEW_PENDING', 'INTERVIEW_SCHEDULED', 'INTERVIEW_COMPLETED', 'PASSED_INTERVIEW', 'FAILED_INTERVIEW'], tone: 'blue', icon: <MessagesSquare size={14} /> },
  { id: 'BACKGROUND_QUEUE',label: 'Background Queue',stages: ['AWAITING_DOCUMENTS', 'DOCUMENT_REVIEW', 'FINAL_DECISION'], tone: 'blue', icon: <ShieldCheck size={14} /> },
  { id: 'HIRING_QUEUE',    label: 'Hired',           stages: ['HIRED'], tone: 'lime', icon: <BadgeCheck size={14} /> },
  { id: 'HISTORICAL',      label: 'Historical',      stages: ['NOT_HIRED', 'ARCHIVED', 'FAILED_INTERVIEW'], tone: 'red', icon: <Archive size={14} /> },
];

const STAGE_LABEL = {
  APPLIED: 'Applied', UNDER_REVIEW: 'Under Review',
  APPROVED_FOR_TESTING: 'Approved Testing', TEST_IN_PROGRESS: 'Testing', AWAITING_TEST_REVIEW: 'Test Review',
  INTERVIEW_PENDING: 'Interview Pending', INTERVIEW_SCHEDULED: 'Interview Set',
  INTERVIEW_COMPLETED: 'Interview Done', PASSED_INTERVIEW: 'Passed Interview', FAILED_INTERVIEW: 'Failed Interview',
  AWAITING_DOCUMENTS: 'Docs Pending', DOCUMENT_REVIEW: 'Docs Review', FINAL_DECISION: 'Final Decision',
  HIRED: 'Hired', NOT_HIRED: 'Not Hired', ARCHIVED: 'Archived',
};

const STAGE_ORDER = [
  'APPLIED', 'UNDER_REVIEW',
  'APPROVED_FOR_TESTING', 'TEST_IN_PROGRESS', 'AWAITING_TEST_REVIEW',
  'INTERVIEW_PENDING', 'INTERVIEW_SCHEDULED', 'INTERVIEW_COMPLETED', 'PASSED_INTERVIEW',
  'AWAITING_DOCUMENTS', 'DOCUMENT_REVIEW', 'FINAL_DECISION',
  'HIRED',
];
const computeProgress = (stage) => {
  if (!stage) return 0;
  if (['NOT_HIRED','ARCHIVED','FAILED_INTERVIEW'].includes(stage)) return 100;
  const idx = STAGE_ORDER.indexOf(stage);
  return idx < 0 ? 5 : Math.round(((idx + 1) / STAGE_ORDER.length) * 100);
};
const computeRisk = (a) => {
  if (a.risk_flag === 'HIGH' || a.risk_flag === 'high') return 'high';
  if (a.risk_flag === 'LOW'  || a.risk_flag === 'low')  return 'low';
  if (a.stage === 'FAILED_INTERVIEW' || a.stage === 'NOT_HIRED') return 'high';
  if (a.test_score != null) {
    if (a.test_score >= 80) return 'low';
    if (a.test_score <  60) return 'high';
    return 'medium';
  }
  return 'medium';
};

// =====================================================================
const ADMIN_ROLES = new Set(['main_hub', 'department_admin', 'training_director', 'super_admin']);

export default function RecruitmentCommandCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeBucket, setActiveBucket] = useState('ALL');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [riskFilter, setRiskFilter] = useState('ALL');

  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;

  const loadPipeline = async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/applicants/pipeline`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPipeline(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) { setLoading(false); return; }
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/applicants/pipeline`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setPipeline(data);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  const counts = useMemo(() => {
    const c = {};
    if (!pipeline?.groups) return BUCKETS.reduce((a, b) => ({ ...a, [b.id]: 0 }), {});
    const allTotal = Object.values(pipeline.groups).reduce((s, arr) => s + (arr?.length || 0), 0);
    for (const b of BUCKETS) {
      if (b.stages === null) { c[b.id] = allTotal; continue; }
      c[b.id] = b.stages.reduce((acc, s) => acc + (pipeline.groups[s]?.length || 0), 0);
    }
    return c;
  }, [pipeline]);

  const applicants = useMemo(() => {
    if (!pipeline?.groups) return [];
    const bucket = BUCKETS.find(b => b.id === activeBucket) || BUCKETS[0];
    // ALL bucket → flatten every stage in pipeline
    const stagesToUse = bucket.stages === null ? Object.keys(pipeline.groups || {}) : bucket.stages;
    let list = stagesToUse.flatMap(s => (pipeline.groups[s] || []).map(r => ({ ...r, _stage: s })));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(a =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.position || '').toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q)
      );
    }
    list.sort((x, y) => (y.last_activity_at || '').localeCompare(x.last_activity_at || ''));
    // Risk filter
    if (riskFilter !== 'ALL') {
      list = list.filter(a => computeRisk(a) === riskFilter.toLowerCase());
    }
    return list.map(a => ({
      id: a.id,
      name: a.name || 'Unnamed Applicant',
      application_id: a.id ? `APP-${String(a.id).slice(0, 8).toUpperCase()}` : '',
      position: a.position || 'Field Investigator',
      recruiter: a.assigned_reviewer_name || a.recruiter_name || null,
      risk: computeRisk(a),
      progress_pct: computeProgress(a._stage || a.stage),
      stage_label: STAGE_LABEL[a._stage || a.stage] || (a._stage || a.stage || '').replace(/_/g, ' '),
      stage: a._stage || a.stage,
      test_score: a.test_score,
      last_activity_at: a.last_activity_at,
      photo_url: a.photo_url || null,
    }));
  }, [pipeline, activeBucket, query, riskFilter]);

  // count anything that's overdue/at-risk for alert ribbon
  const alerts = useMemo(() => {
    if (!pipeline?.groups) return 0;
    return (pipeline.groups['AWAITING_TEST_REVIEW']?.length || 0)
         + (pipeline.groups['FINAL_DECISION']?.length || 0);
  }, [pipeline]);

  useEffect(() => {
    if (!selectedId) return;
    // detail is now loaded inside ApplicantDetailDrawer
  }, [selectedId]);

  const refresh = () => loadPipeline();
  const activeBucketObj = BUCKETS.find(b => b.id === activeBucket) || BUCKETS[0];

  // Master Directive #30 — selective roster export.
  // When selectionMode is on the queue renders a checkbox in front of every
  // applicant card. Only checked applicants land in the CSV. When off the
  // export behaves as before (whole bucket).
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(applicants.map(a => a.id)));
  const clearSelection = () => setSelectedIds(new Set());

  // Export current bucket (or selected subset) to CSV
  const exportRoster = () => {
    let subset = applicants;
    if (selectionMode) {
      if (!selectedIds.size) { toast.error('Select at least one applicant to export'); return; }
      subset = applicants.filter(a => selectedIds.has(a.id));
    }
    if (!subset.length) { toast.error('Nothing to export — bucket is empty'); return; }
    const header = ['Application ID', 'Name', 'Position', 'Stage', 'Risk', 'Test Score', 'Last Activity'];
    const rows = subset.map(a => [
      a.application_id, a.name, a.position, a.stage_label, a.risk,
      a.test_score != null ? `${Math.round(a.test_score)}%` : '',
      a.last_activity_at || '',
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const suffix = selectionMode ? 'selected' : activeBucketObj.id.toLowerCase();
    link.download = `nexus-roster-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} applicants`);
  };

  return (
    <div className="nx" data-testid="recruitment-command-center">
      {/* ============ HEADER ============ */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="nx-eyebrow" data-testid="rcc-eyebrow">Nexus // Recruitment Command</div>
          <h1 className="nx-h1 mt-1" data-testid="rcc-title">Recruitment Command Center</h1>
          <p className="nx-body mt-1" style={{ color: 'var(--nx-ink-soft)' }}>
            Live applicant intelligence · <span className="nx-mono" style={{ fontWeight: 700, color: 'white' }}>{pipeline?.total ?? 0}</span> total candidates in pipeline
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <NxButton variant="secondary" onClick={() => navigate('/admin/certifications')} data-testid="rcc-cert-admin">
              <ScrollText size={14} /> Certificate Administration
            </NxButton>
          )}
          {isAdmin && (
            <NxButton variant="secondary" onClick={() => navigate('/nexus-new-hire/tams-manage')} data-testid="rcc-tams-manage">
              <GraduationCap size={14} /> TAMS Manage
            </NxButton>
          )}
          <NxButton variant="ghost" onClick={refresh} data-testid="rcc-refresh">
            <RefreshCw size={14} /> Refresh
          </NxButton>
          <NxButton
            variant={selectionMode ? 'primary' : 'secondary'}
            onClick={() => { setSelectionMode(m => !m); clearSelection(); }}
            data-testid="rcc-select-toggle"
            title="Toggle selection mode for the roster export"
          >
            <CheckSquare size={14} /> {selectionMode ? `Selected: ${selectedIds.size}` : 'Select'}
          </NxButton>
          {selectionMode && (
            <>
              <NxButton variant="ghost" className="nx-btn--sm" onClick={selectAll} data-testid="rcc-select-all">
                Select All
              </NxButton>
              <NxButton variant="ghost" className="nx-btn--sm" onClick={clearSelection} data-testid="rcc-select-clear">
                Clear
              </NxButton>
            </>
          )}
          <NxButton variant="secondary" onClick={exportRoster} data-testid="rcc-export">
            <Download size={14} /> {selectionMode ? `Export ${selectedIds.size || 'Selected'}` : 'Export Roster'}
          </NxButton>
          <NxButton variant="primary" onClick={() => navigate('/apply')} data-testid="rcc-new-applicant">
            <Plus size={14} /> New Applicant
          </NxButton>
        </div>
      </div>

      {/* ============ ALERT STRIP ============ */}
      {alerts > 0 && (
        <div className="mb-5">
          <NxAlertStrip
            tone="amber"
            icon={<Siren size={18} />}
            title={`Action Required · ${alerts} candidate${alerts !== 1 ? 's' : ''} need review`}
            body="Awaiting Test Review or Final Decision — review and advance to keep the pipeline moving."
            action={<NxButton variant="secondary" className="nx-btn--sm" style={{ background: 'rgba(0,0,0,0.15)', borderColor: 'rgba(0,0,0,0.20)', color: '#1A1300' }}>Review</NxButton>}
            data-testid="rcc-alert"
          />
        </div>
      )}

      {/* ============ LIVE STAT TILES ============ */}
      <div className="grid gap-3 mb-5"
           style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
           data-testid="rcc-ticker">
        {BUCKETS.map(b => (
          <NxTickerTile
            key={b.id}
            label={b.label}
            value={String(counts[b.id] ?? 0).padStart(2, '0')}
            hint={b.id === activeBucket ? 'Currently viewing' : 'Click to view'}
            tone={b.tone}
            icon={b.icon}
            active={b.id === activeBucket}
            onClick={() => { setActiveBucket(b.id); setSelectedId(null); }}
            data-testid={`rcc-ticker-${b.id.toLowerCase()}`}
          />
        ))}
      </div>

      {/* ============ MAIN MDT GRID ============ */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1fr' }}>

        {/* ===== LEFT — applicant queue ===== */}
        <NxPanel
          title={activeBucketObj.label}
          subtitle={`${applicants.length} candidate${applicants.length !== 1 ? 's' : ''} in queue`}
          padded={false}
          action={
            <div className="flex items-center gap-2">
              <label className="nx-search" style={{ width: 220, height: 34 }}>
                <Search size={14} style={{ color: 'var(--nx-ink-dim)' }} />
                <input
                  data-testid="rcc-search-input"
                  placeholder="Search name, position, email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ fontSize: 13 }}
                />
              </label>
              <NxButton variant="ghost" className="nx-btn--sm" onClick={() => setFilterOpen(o => !o)} data-testid="rcc-filter">
                <Filter size={14} /> Filter {riskFilter !== 'ALL' && `· ${riskFilter}`}
              </NxButton>
              {filterOpen && (
                <select
                  value={riskFilter}
                  onChange={e => { setRiskFilter(e.target.value); setFilterOpen(false); }}
                  className="bg-[#0E1626] border border-slate-700 text-xs text-white rounded px-2 py-1"
                  data-testid="rcc-filter-select"
                >
                  <option value="ALL">All Risk</option>
                  <option value="HIGH">High Risk</option>
                  <option value="MEDIUM">Medium Risk</option>
                  <option value="LOW">Low Risk</option>
                </select>
              )}
            </div>
          }
          data-testid="rcc-queue-panel"
        >
          <div className="nx-panel-body" style={{ maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
            {loading ? (
              <p className="nx-body" style={{ color: 'var(--nx-ink-dim)' }}>Loading pipeline…</p>
            ) : error ? (
              <div className="space-y-1">
                <p className="nx-h3" style={{ color: 'var(--nx-red)' }}>Error · {error}</p>
                <p className="nx-body">You may not have hiring-admin permissions on this account.</p>
              </div>
            ) : applicants.length === 0 ? (
              <div className="flex flex-col items-center text-center py-10 gap-2">
                <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--nx-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Briefcase size={22} style={{ color: 'var(--nx-ink-dim)' }} />
                </div>
                <h3 className="nx-h3">No candidates in this queue</h3>
                <p className="nx-body">Applicants will appear here as soon as they enter <b>{activeBucketObj.label}</b>.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="rcc-applicant-list">
                {applicants.map(a => (
                  <div key={a.id} className="flex items-start gap-3">
                    {selectionMode && (
                      <label className="pt-3 pl-2 flex items-center cursor-pointer" title="Include in selective export">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(a.id)}
                          onChange={() => toggleSelected(a.id)}
                          className="w-4 h-4 rounded border-slate-500 accent-amber-400 cursor-pointer"
                          data-testid={`rcc-select-${a.id}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </label>
                    )}
                    <div className="flex-1 min-w-0">
                      <NxApplicantCard
                        applicant={a}
                        active={a.id === selectedId}
                        onOpen={(id) => selectionMode ? toggleSelected(id) : navigate(`/recruitment/applicants/${id}`)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </NxPanel>

        {/* Applicant review has been moved to full-page /recruitment/applicants/:id */}
      </div>
    </div>
  );
}
